<script>
import { useClientStore, useRouterStore, FormMixin } from '@artup/client'
import { DocumentType } from '@artup/model'
import { wait } from '@artup/utilities'
import { mapState, mapActions } from 'pinia'
import { useAccountsStore } from '../../../store'
import AccountsForm from '../../../components/accounts-form.vue'
import AccountsMixin from '../../../components/accounts-mixin'
import TermsAndConditions from '../../../assets/terms-and-conditions-artup.md?raw'

export default {
  mixins: [
    FormMixin,
    AccountsMixin
  ],

  components: {
    'artup-accounts-view': AccountsForm
  },

  data () {
    return {
      // Entered account name
      name: '',
      // Indicates whether signup has been attempted
      signupAttempted: false,
      // Indicates whether signup attempt has succeded
      signupSucceeded: false,
      // Already initiated account request
      pendingRequest: false,
      // Time in minutes when another request can be issued
      timeToExpire: false,
      // Existing account, populated when existing user tries to sign up again
      account: null,
      // User accepted Terms and Conditions and newsletter subscription
      acceptTerms: false,
      subscribeNewsletter: true,
      // Visibility of Terms and Conditions document
      termsVisible: false,
      // The content of Terms and Conditions document
      TermsAndConditions,
      DocumentType
    }
  },

  computed: {
    ...mapState(useClientStore, [
      'isBusy'
    ]),

    ...mapState(useRouterStore, [
      'currentRoute'
    ]),

    // Dialog title
    title () {
      const { isBusy, signupAttempted, signupSucceeded, pendingRequest, account } = this
      if (signupAttempted) {
        if (isBusy) return 'Creating Your Account'
        if (pendingRequest) return 'Pending Request'
        if (signupSucceeded) return 'Sign Up Succeeded'
        if (account) return 'Account already exists'
        return 'Sign Up Failed'
      }
      return 'Join the ARTup revolution'
    },

    // Dialog subtitle
    subtitle () {
      const { isBusy, signupAttempted } = this
      if (!(signupAttempted || isBusy)) {
        return 'Create an account'
      }
    },

    // Indicates whether Continue button should be enabled
    canContinue () {
      const { name, acceptTerms: confirmTerms, isBusy, signupAttempted } = this
      return name &&
        confirmTerms &&
        !isBusy &&
        !signupAttempted
    }
  },

  methods: {
    ...mapActions(useClientStore, [
      'busy',
      'done'
    ]),

    ...mapActions(useAccountsStore, [
      'signup'
    ]),

    // Populates the dialog
    populate ({ name } = {}) {
      this.name = name || ''
    },

    // Attempts the signup
    async trySignup () {
      this.secure(async reCaptchaToken => {
        const { name, subscribeNewsletter } = this
        if (await this.validate()) {
          if (name) {
            try {
              await this.busy()
              this.signupAttempted = true
              await wait(2000)
              const { token, pendingRequest, timeToExpire, account } = await this.signup({
                name,
                reCaptchaToken,
                subscribeNewsletter,
                sendEmail: true
              })
              this.signupSucceeded = token != null
              this.pendingRequest = pendingRequest
              this.timeToExpire = timeToExpire
              this.account = account

            } catch {
              this.signupSucceeded = false
              this.pendingRequest = null
              this.timeToExpire = null
              this.account = null

            } finally {
              await this.done()
            }
          }
        }
      })
    },

    // Resets the dialog for another attempt
    reset () {
      this.name = null
      this.signupAttempted = false
      this.signupSucceeded = false
      this.pendingRequest = null
      this.timeToExpire = null
      this.acceptTerms = false
      this.subscribeNewsletter = true
      this.account = null
    }
  },

  async beforeRouteUpdate (to) {
    await this.populate({ name: to.query.name })
  },

  async created () {
    await this.populate({ name: this.currentRoute.query.name })
  }
}

</script>

<template>
  <artup-accounts-view v-cloak class="activate" :title="title" :subtitle="subtitle">

    <template #content>
      <div v-if="isBusy" class="form q-mt-sm">
        <div class="fields">
          <div class="row items-center justify-center">
            <q-icon name="settings" class="rotate-reverse-slow q-mr-sm" color="indigo-8" size="lg">
            </q-icon>
            <span>
              Please wait ...
            </span>
          </div>
        </div>
      </div>

      <q-form v-else-if="!signupAttempted" ref="form" class="form">
        <div class="fields">
          <q-input label="E-mail address" class="input-name" outlined v-model="name" maxlength="45"
            @keydown.enter="trySignup()">
          </q-input>
        </div>

        <div class="terms q-mt-lg column">
          <q-checkbox v-model="acceptTerms">
            <label>
              I agree to the
            </label>
            <a @click.stop="termsVisible = true">
              ARTup Terms and Conditions
            </a>
          </q-checkbox>
          <q-checkbox v-model="subscribeNewsletter">
            <label>
              I want to subscribe to the ARTup newsletters
            </label>
          </q-checkbox>
        </div>

        <div class="buttons q-mt-lg">
          <q-btn class="button-signup bg-grey-9 text-white q-pb-md q-pt-md" label="Continue" unelevated rounded no-caps
            @click="trySignup()" :disable="!canContinue">
          </q-btn>
        </div>

      </q-form>

      <div v-else-if="signupAttempted && signupSucceeded" class="form wider">
        <div class="fields column items-center">
          <span class="q-mt-lg">
            Account <b>{{ name }}</b> has been created.
          </span>
          <span>
            Please check your mailbox for further instructions.
          </span>
        </div>
        <div class="buttons q-mt-lg q-pl-xl q-pr-xl">
          <q-btn class="button-login bg-grey-9 text-white q-pb-md q-pt-md" label="Continue to Login" unelevated rounded
            no-caps :to="{ name: 'login', query: { name } }">
          </q-btn>
        </div>
      </div>

      <div v-else-if="signupAttempted && pendingRequest" class="form wider">
        <div class="fields column items-center">
          <q-icon name="schedule" color="blue-8" size="xl">
          </q-icon>
          <span class="q-mt-lg">
            It looks like you have created an account.
            We have sent you an e-mail with instructions.
            If you can't find it, please try again in {{ timeToExpire }}
            {{ pluralize(timeToExpire, 'minute.', 'minutes.') }}
          </span>
        </div>
        <div class="buttons q-mt-lg q-pl-xl q-pr-xl">
          <q-btn class="button-save bg-grey-9 text-white q-pb-md q-pt-md" label="Start the Sign Up again" unelevated
            rounded no-caps @click="reset()">
          </q-btn>
        </div>
      </div>

      <div v-else-if="account" class="form wider q-mt-sm">
        <div class="fields column items-center">
          <q-icon name="info" color="green-8" size="xl">
          </q-icon>
          <span class="q-mt-lg text-center">
            You already have an ARTup account.
            Click below to log in.
            <br>
            If you forgot your password,
            you can reset it on the login page.
          </span>
        </div>
        <div class="buttons q-mt-lg q-pl-xl q-pr-xl">
          <q-btn class="button-login bg-grey-9 text-white q-pb-md q-pt-md" label="Continue to Login" unelevated rounded
            no-caps :to="{ name: 'login', query: { name } }">
          </q-btn>
        </div>
      </div>

      <div v-else class="form wider q-mt-sm">
        <div class="fields column items-center">
          <q-icon name="warning" color="red-8" size="xl">
          </q-icon>
          <span class="q-mt-lg">
            We apologize for the inconvenience.
          </span>
          <span>
            Please try again at later time.
          </span>
        </div>
        <div class="buttons q-mt-lg q-pl-xl q-pr-xl">
          <q-btn class="button-save bg-grey-9 text-white q-pb-md q-pt-md" label="Start the Sign Up Again" unelevated
            rounded no-caps @click="reset()">
          </q-btn>
        </div>
      </div>
    </template>
  </artup-accounts-view>

  <div class="terms-and-conditions" v-if="termsVisible">
    <main>
      <div class="content">
        <artup-document-viewer :content="TermsAndConditions" :type="DocumentType.Markdown">
        </artup-document-viewer>
      </div>
      <div class="buttons q-mb-lg">
        <q-btn class="button-close-terms bg-grey-9 text-white q-pb-md q-pt-md" label="Close" unelevated rounded no-caps
          @click.stop="termsVisible = false">
        </q-btn>
      </div>
    </main>
  </div>

</template>

<style scoped lang="scss">
.terms {
  display: flex;
  flex-direction: column;
  padding-left: 10px;
}

.terms-and-conditions {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
  padding: 20px;

  >main {
    flex: 1;
    width: 100%;
    height: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
    align-items: center;

    .buttons {
      width: 100%;
      height: auto;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .content {
      width: 100%;
      max-width: 1200px;
      padding: 20px;
    }

    .button-close-terms {
      width: 250px;
      font-size: 16px;
    }
  }
}

/* Layout adjustments for mobile screens */
@media screen and (max-width: 640px) {
  .terms {
    gap: 10px;
  }

  .terms-and-conditions {
    padding: 5px;

    >main {

      .content {
        max-width: unset;
      }

      .buttons {
        .button-close-terms {
          width: 90%;
        }
      }
    }
  }
}
</style>
